// utils/rssi.ts
import type { Theme } from '@mui/material';
import type { JSX } from 'react/jsx-runtime';
import { createElement } from 'react';
import { DynamicIcon } from 'lucide-react/dynamic';

type RssiClassification = 'Excellent' | 'Good' | 'Regular' | 'Bad' | 'Critical';

const colorByRssiClassification = (
  theme: Theme
): Record<RssiClassification, string> => ({
  Excellent: theme.palette.status.online,
  Good: theme.palette.status.warning,
  Regular: theme.palette.status.warning,
  Bad: theme.palette.status.offline,
  Critical: theme.palette.status.offline,
});

const iconNameByClassification: Record<RssiClassification, string> = {
  Excellent: 'laugh',
  Good: 'smile',
  Regular: 'meh',
  Bad: 'annoyed',
  Critical: 'angry',
};

export const getRssiColor = (
  classification: string | undefined,
  theme: Theme
): string => {
  if (!classification) {
    return theme.palette.grey['05'] ?? '#000000';
  }
  return (
    colorByRssiClassification(theme)[classification as RssiClassification] ??
    theme.palette.grey['05'] ??
    '#000000'
  );
};

export const getRssiIcon = (
  classification: string | undefined,
  theme: Theme
): JSX.Element | null => {
  if (!classification) return null;

  const iconName =
    iconNameByClassification[classification as RssiClassification];
  const color = getRssiColor(classification, theme);

  if (!iconName) return null;

  return createElement(DynamicIcon, {
    name: iconName as Parameters<typeof DynamicIcon>[0]['name'],
    size: 16,
    color,
  });
};
